import { type AxiosRequestConfig, isAxiosError } from 'axios'
import type { UnavailableDocumentStatus } from '@/types/documents'
import type { DocumentMeta } from '@/services/api/site'
import { useCustomerStore } from '@/storage/customer'

export function documentContentUrl(
  meta: Pick<DocumentMeta, 'origin' | 'uuid'>,
): string {
  const { connection } = useCustomerStore()

  // cf-worker-capture-document needs the tenant, so without one only the api's own url can answer.
  if (connection === null) {
    return meta.origin
  }

  const query = new URLSearchParams({ client: connection })

  return `/client/documents/${encodeURIComponent(meta.uuid)}?${query}`
}

/**
 * The request config every content read shares. capture-api answers a document it cannot serve
 * with a 2xx as well as with errors — 202 while the scan is in progress — and axios resolves any
 * 2xx, which would bind a json body into an `<img src>`. Only a 200 carries bytes.
 */
export const documentContentRequest = {
  responseType: 'blob',
  validateStatus: (status: number) => status === 200,
} satisfies AxiosRequestConfig

/**
 * The status a refused content read means: 202 is a scan still running (a document replaced
 * since its reference was read), 422 a threat found, and anything else — 404, 403, a network
 * fault — a document with nothing this user can be shown.
 */
export function refusedContentStatus(
  error: unknown,
): UnavailableDocumentStatus {
  const status = isAxiosError(error) ? error.response?.status : undefined

  if (status === 202) {
    return 'awaiting_scan'
  }

  if (status === 422) {
    return 'threat_found'
  }

  return 'missing'
}
